import { Component, EventEmitter, Input, Output, ViewChild, OnInit, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatAutocompleteTrigger, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SpinnerComponent } from "src/app/components/layout/spinner/spinner.component";
import { MenuService } from 'src/app/services/menu/menu.service';

@Component({
  selector: 'app-parent-menu-select',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    SpinnerComponent
  ],
  templateUrl: './parent-menu-select.component.html',
  styleUrl: './parent-menu-select.component.css'
})
export class ParentMenuSelectComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() allMenu: any[] | null = null;
  @Input() selectedMenuId: number | null = null;
  @Input() excludeMenuId: number | null = null; // current edited item id (should not be selectable as parent)
  @Input() filterPos: any = null;
  @Output() selected = new EventEmitter<number>();

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  searchControl = new FormControl<any>('');
  filteredPages: any[] = [];
  private flatMenuAll: any[] = [];
  private flatMenu: any[] = []; // currently active (filtered) flat list

  load = false;
  error = false;

  constructor(private MenuService: MenuService){}

  ngOnInit() {
    console.log('[ParentMenuSelect] init', {
      selectedMenuId: this.selectedMenuId,
      excludeMenuId: this.excludeMenuId,
      allMenuProvided: Array.isArray(this.allMenu) ? this.allMenu.length : this.allMenu
    });

    // If parent already provided menu tree, use it; otherwise fetch from API.
    if (Array.isArray(this.allMenu) && this.allMenu.length) {
      this.rebuildFlatLists();
      this.applySelectionIfPossible();
      this.listenToSearch();
      return;
    }

    this.load = true;
    this.MenuService.getMenu().subscribe(
      (res: any) => {
        this.load = false;
        // Prefer full grouped payload (admin menu), otherwise fall back to main
        this.allMenu = res?.data ?? (res?.data?.main ?? []);
        console.log('[ParentMenuSelect] menu loaded from API', {
          count: Array.isArray(this.allMenu) ? this.allMenu.length : 0,
          selectedMenuId: this.selectedMenuId,
          excludeMenuId: this.excludeMenuId
        });

        this.rebuildFlatLists();
        this.applySelectionIfPossible();
      },
      (error) => {
        console.log('[ParentMenuSelect] menu load error', error);
        this.load = false;
        this.error = true;
      }
    );

    this.listenToSearch();
  }

  ngOnChanges(changes: SimpleChanges) {
    // When menu tree changes (or becomes available), rebuild using the same pipeline
    if (changes['allMenu'] && Array.isArray(this.allMenu)) {
      console.log('[ParentMenuSelect] allMenu changed', {
        count: this.allMenu.length,
        selectedMenuId: this.selectedMenuId,
        excludeMenuId: this.excludeMenuId,
        filterPos: this.filterPos
      });
      this.rebuildFlatLists();
      this.applySelectionIfPossible();
    }

    // Rebuild when filters/exclusions change
    if ((changes['excludeMenuId'] || changes['filterPos']) && Array.isArray(this.allMenu)) {
      if (changes['excludeMenuId']) {
        console.log('[ParentMenuSelect] excludeMenuId changed', this.excludeMenuId);
      }
      if (changes['filterPos']) {
        console.log('[ParentMenuSelect] filterPos changed', this.filterPos);
      }
      this.rebuildFlatLists();
      this.applySelectionIfPossible();
    }

    // If selection changes, try to apply it
    if (changes['selectedMenuId']) {
      console.log('[ParentMenuSelect] selectedMenuId changed', this.selectedMenuId);
      this.applySelectionIfPossible();
    }
  }

  private getTreeForCurrentPos(): any[] {
    // allMenu may be either:
    //  - an array tree (already the correct section), or
    //  - a grouped object { top, main, bottom, hidden, ... }

    const raw: any = this.allMenu as any;

    // If it's already an array, use as-is.
    if (Array.isArray(raw)) return raw;

    // If it's a grouped object, choose the correct group by filterPos.
    if (raw && typeof raw === 'object') {
      const posNum = (this.filterPos === null || this.filterPos === undefined || this.filterPos === '')
        ? null
        : Number(this.filterPos);

      // Map UI select values to groups
      const key = (posNum === 1) ? 'top'
        : (posNum === 2) ? 'main'
        : (posNum === 3) ? 'bottom'
        : (posNum === 4) ? 'hidden'
        : null;

      if (key && Array.isArray(raw[key])) return raw[key];

      // Fallback: if no key matches, try main
      if (Array.isArray(raw['main'])) return raw['main'];

      // Final fallback: first array value in the object
      for (const v of Object.values(raw)) {
        if (Array.isArray(v)) return v as any[];
      }
    }

    return [];
  }

  ngAfterViewInit() {
    if (this.filteredPages.length && this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  private rebuildFlatLists(): void {
    const tree = this.getTreeForCurrentPos();

    const excludedIds = this.excludeMenuId != null
      ? this.collectSubtreeIds(tree, this.excludeMenuId)
      : new Set<number>();

    // 1) Build full flattened list (keeps depth for indentation)
    const flatAll = this.flattenMenu(tree, 0, excludedIds);
    this.flatMenuAll = flatAll;

    // Debug: how many items exist per pos value
    const posStats = flatAll.reduce((acc: Record<string, number>, x: any) => {
      const p = Number(x?.pos ?? x?.position);
      const key = Number.isNaN(p) ? 'NaN' : String(p);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const posNum = (this.filterPos === null || this.filterPos === undefined || this.filterPos === '')
      ? null
      : Number(this.filterPos);

    const wantedPos = (posNum === 4) ? 3 : posNum;

    let filtered = flatAll;

    if (wantedPos !== null && !Number.isNaN(wantedPos)) {
      filtered = flatAll.filter(x => Number(x?.pos ?? x?.position) === wantedPos);

      // If filter produced 0, fallback to showing all (older rows may not carry pos on children)
      if (filtered.length === 0) filtered = flatAll;
    }


    this.flatMenu = filtered;
    this.filteredPages = [...this.flatMenu];

    console.log('[ParentMenuSelect] rebuilt flat list', {
      flatAllCount: flatAll.length,
      flatCount: this.flatMenu.length,
      excludedCount: excludedIds.size,
      selectedMenuId: this.selectedMenuId,
      excludeMenuId: this.excludeMenuId,
      filterPos: this.filterPos,
      posNum,
      wantedPos,
      posStats,
      treeCount: Array.isArray(tree) ? tree.length : 0,
      emptyAfterFilter: (posNum !== null && !Number.isNaN(posNum) && this.flatMenu.length === 0)
    });
  }

  private applySelectionIfPossible(): void {
    if (this.selectedMenuId == null) return;

    const idNum = typeof this.selectedMenuId === 'string' ? Number(this.selectedMenuId) : this.selectedMenuId;
    if (!idNum) {
      // 0 means "no parent" (root). Keep input empty.
      this.searchControl.setValue('', { emitEvent: false });
      console.log('[ParentMenuSelect] applySelectionIfPossible', { idNum, found: false, visibleInFiltered: false, filterPos: this.filterPos, note: 'root (no parent)' });
      return;
    }

    // Find selection in the *flat list* so MatAutocomplete can highlight correctly.
    const selectedFromAll = this.flatMenuAll.find(p => (p?.id == idNum || p?.page == idNum)) || null;
    const visibleInFiltered = this.flatMenu.find(p => (p?.id == idNum || p?.page == idNum)) ? true : false;

    console.log('[ParentMenuSelect] applySelectionIfPossible', {
      idNum,
      found: !!selectedFromAll,
      visibleInFiltered,
      filterPos: this.filterPos,
      note: (!visibleInFiltered && this.filterPos !== null && this.filterPos !== undefined && this.filterPos !== '')
        ? 'selected id is not in filtered list (likely different pos)'
        : ''
    });

    if (selectedFromAll) {
      this.searchControl.setValue(selectedFromAll, { emitEvent: false });
    }
  }

  private collectSubtreeIds(list: any[], rootId: number): Set<number> {
    const found = this.findPageRecursive(list, rootId);
    const ids = new Set<number>();
    if (!found) return ids;

    const walk = (node: any) => {
      const id = Number(node?.id ?? node?.page);
      if (!Number.isNaN(id)) ids.add(id);
      const children = node?.children || node?.child || node?.items || node?.pages;
      if (Array.isArray(children)) {
        for (const c of children) walk(c);
      }
    };

    walk(found);
    return ids;
  }

  private flattenMenu(list: any[], depth = 0, excludeIds: Set<number> = new Set()): any[] {
    const flat: any[] = [];
    for (const item of list) {
      if (!item) continue;
      const itemId = Number(item?.id ?? item?.page);
      if (!Number.isNaN(itemId) && excludeIds.has(itemId)) {
        continue; // skip current edited item (and its descendants)
      }

      flat.push({ ...item, depth }); // keep depth for indentation
      if (item.children && item.children.length) {
        flat.push(...this.flattenMenu(item.children, depth + 1, excludeIds));
      }
    }
    return flat;
  }

  private findPageRecursive(list: any[], id: number): any | null {
    if (!Array.isArray(list)) return null;
    for (const p of list) {
      if (p == null) continue;
      // match common id fields (id, page) and tolerate string/number
      if (p.id == id || p.page == id) return p;
      // some APIs use nested children arrays under different keys
      const children = p.children || p.child || p.items || p.pages;
      if (children && Array.isArray(children)) {
        const found = this.findPageRecursive(children, id);
        if (found) return found;
      }
    }
    return null;
  }

  setSelectedPage(pageId: number) {
    // legacy helper kept for manual calls; prefer applySelectionIfPossible()
    if (pageId == null) return;
    const idNum = typeof pageId === 'string' ? Number(pageId) : pageId;
    const selectedFromFlat = this.flatMenu.find(p => (p?.id == idNum || p?.page == idNum)) || null;

    console.log('[ParentMenuSelect] setSelectedPage', { idNum, found: !!selectedFromFlat });

    if (selectedFromFlat) {
      this.searchControl.setValue(selectedFromFlat, { emitEvent: false });
    }
  }

  filterPages(query: any = '') {
    const safeQuery = String(query || '').toLowerCase().trim();

    this.filteredPages = !safeQuery
      ? [...this.flatMenu]
      : this.flatMenu.filter((p) =>
          (p?.title || '').toLowerCase().includes(safeQuery)
        );
  }

  listenToSearch() {
    this.searchControl.valueChanges.subscribe((value) => {
      // When user types
      if (typeof value === 'string' || value === '') {
        this.filterPages(value);
        if (this.filteredPages.length && this.autocompleteTrigger) {
          this.autocompleteTrigger.openPanel();
        }
        return;
      }

      // When user selects an option (object)
      // keep current selection; no need to re-filter
    });
  }

  onSelect(value: any) {
    const raw = value?.id ?? value?.page ?? 0;
    const id = Number(raw) || 0;
    console.log('[ParentMenuSelect] selected parent', { id, raw, filterPos: this.filterPos, value });
    this.selectedMenuId = id;
    this.selected.emit(id);
  }

  displayFn = (page: any) => {
    return page?.title || '';
  };

  onInputClick(): void {
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }
}
